import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Contract } from "../types";
import { useToast } from "../toast";
import { fmtDate } from "../format";
import ContractDialog from "../dialogs/ContractDialog";
import ConfirmDialog from "../dialogs/ConfirmDialog";

export default function Contracts() {
  const api = useAuthApi();
  const [items, setItems] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [deleting, setDeleting] = useState<Contract | null>(null);
  const [search, setSearch] = useState("");
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<Contract[]>(`${MGMT}/contracts`);
      setItems(data ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(`${MGMT}/contracts/${deleting.id}`, { method: "DELETE" });
      toast.show("Contract deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const chargerLabel = (c: Contract) => {
    if (c.plan?.charger_config) return c.plan.charger_config.display_name ?? c.plan.charger_config.charge_point_id;
    if (c.plan?.charger_group) return `Group: ${c.plan.charger_group.name}`;
    return "—";
  };

  const tariffLabel = (c: Contract) => {
    if (c.plan?.tariff) return `${c.plan.tariff.name} (${c.plan.tariff.currency})`;
    return "Charger default";
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) => {
      const haystack = [
        c.name,
        c.plan?.name,
        c.driver?.name,
        c.driver?.email,
        c.code_used,
        c.is_active ? "active" : "inactive",
        chargerLabel(c),
        tariffLabel(c),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, search]);

  const hasFilters = search.trim().length > 0;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
        >
          New Contract
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Filter contracts…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {hasFilters && <Button size="small" onClick={() => setSearch("")}>Clear filters</Button>}
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Charger</TableCell>
              <TableCell>Tariff</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Valid</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      {hasFilters
                        ? "No contracts match the current filters."
                        : "No contracts yet. Create a plan first, then subscribe drivers to it."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{c.name ?? `#${c.id}`}</TableCell>
                <TableCell>
                  {c.plan ? (
                    <Chip label={c.plan.name} size="small" variant="outlined" />
                  ) : "—"}
                </TableCell>
                <TableCell>{c.driver ? `${c.driver.name}` : `#${c.driver_id}`}</TableCell>
                <TableCell>{chargerLabel(c)}</TableCell>
                <TableCell>{tariffLabel(c)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                  {c.code_used ?? "—"}
                </TableCell>
                <TableCell sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                  {c.valid_from || c.valid_until
                    ? `${c.valid_from ? fmtDate(c.valid_from) : "∞"} – ${c.valid_until ? fmtDate(c.valid_until) : "∞"}`
                    : "Always"}
                </TableCell>
                <TableCell>
                  <Chip
                    label={c.is_active ? "Active" : "Inactive"}
                    color={c.is_active ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(c)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(c)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ContractDialog
        open={creating || editing !== null}
        contract={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          load();
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete contract?"
        message={`Delete contract "${deleting?.name ?? `#${deleting?.id}`}"? This will prevent the driver from charging.`}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
